/**
 * Server-anchored clock.
 *
 * The in-store TVs (Tizen / Visual Art player) are not guaranteed to keep
 * accurate time: clocks drift, get set by hand, or boot before NTP has run.
 * Every time-based decision on the screen - the REST lookback window, the
 * completed-order retention sweep and the scheduled-order lead-time gate -
 * must therefore be made in *server* time, never raw `Date.now()`.
 *
 * We learn the offset between the device clock and the server clock from
 * the `x-server-time` header the /api/orders proxy stamps on every response
 * (falling back to the standard HTTP `Date` header) and apply that offset to
 * `Date.now()`. Until the first calibration `now()` simply equals `Date.now()`.
 *
 * Chrome 56 note: no `performance.now()` here. The local `Date.now()` is read
 * for both the request start and the response receipt, so any device clock
 * error cancels out of the round-trip estimate.
 */

const SERVER_TIME_HEADER = 'x-server-time';

let offsetMs = 0;
let calibrated = false;

/** Current time in *server* milliseconds since the epoch. */
export function now(): number {
  return Date.now() + offsetMs;
}

/**
 * Learned correction in milliseconds: positive when the device clock is
 * behind the server, negative when it is ahead. Zero until calibrated.
 */
export function offset(): number {
  return offsetMs;
}

export function isCalibrated(): boolean {
  return calibrated;
}

/**
 * Learn the offset from a server timestamp. `requestStartedMs` and
 * `responseReceivedMs` are local `Date.now()` readings taken around the
 * request; the server produced its timestamp roughly halfway through the
 * round trip, so that midpoint is the local time to compare against.
 */
export function calibrate(
  serverMs: number,
  requestStartedMs: number,
  responseReceivedMs: number = Date.now(),
): void {
  if (
    !Number.isFinite(serverMs) ||
    !Number.isFinite(requestStartedMs) ||
    !Number.isFinite(responseReceivedMs)
  ) {
    return;
  }
  const localAtServerTime = (requestStartedMs + responseReceivedMs) / 2;
  offsetMs = Math.round(serverMs - localAtServerTime);
  calibrated = true;
}

/**
 * Read the server time off a fetch `Response`. Prefers our own millisecond
 * `x-server-time` header, then the HTTP `Date` header (second precision, set
 * by Vercel and by the Vite dev server). Silently does nothing when neither
 * is readable - a missing header must never break an orders fetch.
 */
export function calibrateFromResponse(res: Response, requestStartedMs: number): void {
  const headers = res ? res.headers : undefined;
  if (!headers || typeof headers.get !== 'function') return;
  const raw = headers.get(SERVER_TIME_HEADER) || headers.get('date');
  if (!raw) return;
  const serverMs = Date.parse(raw);
  if (!Number.isFinite(serverMs)) return;
  calibrate(serverMs, requestStartedMs, Date.now());
}

/** Test helper - forget any learned offset. */
export function reset(): void {
  offsetMs = 0;
  calibrated = false;
}
